import { Component, computed, HostBinding, input, InputSignal, OnDestroy, OnInit, output, OutputEmitterRef, Signal, signal } from '@angular/core';
import { TooltipPosition } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { ButtonType } from 'src/app/features/shared/controls/models/button-type';
import { ButtonColor, resolveButtonColorVar } from 'src/app/features/shared/controls/models/button-color';
import { LoadKey } from '../../../../infrastructure/services/loading-services/models/loadKey';
import { LoadingService as GeneralLoadingService } from 'src/app/infrastructure/services/loading-services/_loading.service';
import { IButtonData } from 'src/app/features/shared/controls/models/_base-button';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

/**
 * Coerce een nullable-boolean input zodat zowel boolean `false` als de string
 * `'false'` als `false` tellen. `null`/`undefined` blijft "niet gezet" (default-gedrag).
 * Elke andere (niet-lege) string telt als `true`, conform Angular's `booleanAttribute`.
 */
function coerceNullableBoolean(value: boolean | string | null | undefined): boolean | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value !== 'false';
    return value;
}

@Component({
    selector: 'app-button',
    template: '',
    standalone: true,
    styleUrls: [],
})
export abstract class _BaseButtonComponent
    implements OnInit, OnDestroy
{

    private __loadingSubscr?: Subscription;
    private __isLoading$$ = signal<boolean>(false);

    protected _generalLoadingService = injectRequired(GeneralLoadingService);

    abstract autofocus$$: Signal<boolean | null>;
    abstract buttonData$$: Signal<IButtonData | null>;
    abstract class$$: Signal<string | string[] | null>;
    abstract color$$: Signal<ButtonColor | null>;
    abstract disabled$$: Signal<boolean | null>;
    abstract fontSize$$: Signal<string | null>;
    abstract formName$$: Signal<string | null>;
    abstract hide$$: Signal<boolean | null>;
    abstract hover$$: Signal<boolean | null>;
    abstract loadKey$$: Signal<LoadKey | LoadKey[] | null>;
    abstract lockOnLoad$$: Signal<boolean | null>;
    abstract style$$: Signal<string | Record<string, string> | null>;
    abstract tabIndex$$: Signal<string | null>;
    abstract text$$: Signal<string | null>;
    abstract tooltip$$: Signal<string | null>;
    abstract tooltipPosition$$: Signal<TooltipPosition | null>;
    abstract type$$: Signal<ButtonType | null>;

    abstract btnClickEmit: OutputEmitterRef<void>;

    abstract ngOnInit(): void;
    abstract ngOnDestroy(): void;

    protected _input_autofocus$$       = input<boolean | null>(null, {alias: 'autofocus'});
    protected _input_buttonData$$      = input<IButtonData | null>(null, {alias: 'buttonData'});
    protected _input_class$$           = input<string | string[] | null>(null, {alias: 'class'});
    protected _input_color$$           = input<ButtonColor | null>(null, {alias: 'color'});
    protected _input_disabled$$        = input<boolean | null>(null, {alias: 'disabled'});
    protected _input_fontSize$$        = input<string | null>(null, {alias: 'fontSize'});
    protected _input_formName$$        = input<string | null>(null, {alias: 'formName'});
    protected _input_hide$$            = input<boolean | null>(null, {alias: 'hide'});
    protected _input_hover$$           = input<boolean | null, boolean | string | null | undefined>(null, {alias: 'hover', transform: coerceNullableBoolean});
    protected _input_loadKey$$         = input<LoadKey | LoadKey[] | null>(null, {alias: 'loadKey'});
    protected _input_lockOnLoad$$      = input<boolean | null>(null, {alias: 'lockOnLoad'});
    protected _input_style$$           = input<string | Record<string, string> | null>(null, {alias: 'style'});
    protected _input_tabIndex$$        = input<string | null>(null, {alias: 'tabIndex'});
    protected _input_text$$            = input<string | null>(null, {alias: 'text'});
    protected _input_tooltip$$         = input<string | null>(null, {alias: 'tooltip'});
    protected _input_tooltipPosition$$ = input<TooltipPosition | null>('above', {alias: 'tooltipPosition'});
    protected _input_type$$            = input<ButtonType | null>(null, {alias: 'type'});

    protected _out_btnClickEmit = output({alias: 'btnClick'});

    // Host-class voor de globale no-hover-regel; via @HostBinding zodat alle concrete
    // knop-varianten (die hun eigen host-config hebben) hem erven.
    @HostBinding('class.no-hover') protected get _noHoverClass(): boolean {
        return this.hover$$() === false;
    }
    // Per-instance knop-schaal als inline style: wint van consumer-CSS én van de
    // font-size-token (specificity-0 :where-regel in styles/components/_button-sizes.scss).
    // null = geen inline style → de cascade bepaalt de maat.
    @HostBinding('style.fontSize') protected get _fontSizeStyle(): string | null {
        return this.fontSize$$();
    }
    protected _disabled$$ = computed(() => {
        const base       = this.disabled$$() ?? this.buttonData$$()?.disabled ?? null;
        const lockOnLoad = this.lockOnLoad$$();
        const loading    = this.__isLoading$$();
        return (lockOnLoad === true && loading) ? true : base;
    });

    // Resolveert `color` (basiskleur of nuance, bv. `color4`/`color4-container`) naar de
    // accent-CSS-var. Wordt door icoon-dragende varianten als `--btn-icon-color` op de
    // knop gezet; via currentColor kleurt dat alléén het geneste app-icon.
    protected _iconColor$$ = computed(() => resolveButtonColorVar(this.color$$()));

    // disabledInteractive (templates): een disabled knop blijft hover-/focusbaar zodat de tooltip
    // uitleg kan geven (bv. structure-locked, niets te bewerken). Material houdt de klik dan bij een
    // <button> niet zelf tegen (alleen bij <a>), dus hier: niet emitten en niet laten doorbubbelen
    // (ook geen native submit via [attr.form]).
    emit_onClick(event?: Event) {
        if (this._disabled$$()) {
            event?.preventDefault();
            event?.stopImmediatePropagation();
            return;
        }
        this.btnClickEmit.emit();
    }

    protected _ngOnInit() {
        const __loadKey = this.loadKey$$();
        this.__loadingSubscr = this._generalLoadingService.isBaseLoading$({key: __loadKey}).subscribe(loading => {
            this.__isLoading$$.set(loading);
        });
    }

    protected _ngOnDestroy() {
        this.__loadingSubscr?.unsubscribe();
    }
}
